import { useState } from "react";
import PageBanner from "@/components/feature/PageBanner";

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: "88c346b5-34bf-479d-8eb6-cf1204cdea5f",
          subject: "New NeoRoop Contact Form Submission",
          from_name: "NeoRoop Website",
          name: formData.get("name"),
          phone: formData.get("phone"),
          city: formData.get("city"),
          message: formData.get("message"),
        }),
      });
      const data = await res.json();
      if (data.success) setSubmitted(true);
    } catch {
      // silently handle
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageBanner
        title="Get in Touch"
        subtitle="We would love to hear from you. Reach out anytime."
        bgImage="/images/contact-banner.jpg"
      />

      <section className="py-10 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            {/* Contact cards */}
            <div className="mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-accent-800 mb-4 text-center">
                How to reach us
              </h2>
              <p className="text-foreground-600 text-sm md:text-base text-center max-w-lg mx-auto leading-relaxed">
                To place an order, tap any WhatsApp button and share your details — we&rsquo;ll help you complete your order. For general questions, fill out the form below and we&rsquo;ll reply within 24 hours.
              </p>
            </div>

            {/* Contact form */}
            <div className="bg-background-100 rounded-2xl p-6 md:p-10 shadow-sm border border-background-200/70">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-accent-800 mb-6">
                Send us a message
              </h2>

              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mb-4">
                    <i className="ri-check-line w-7 h-7 flex items-center justify-center text-2xl" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-accent-800 mb-2">
                    Message sent!
                  </h3>
                  <p className="text-foreground-600 text-sm">
                    Thank you for reaching out. We will get back to you soon.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="contact-form"
                  className="flex flex-col gap-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-1.5">
                      <label
                        htmlFor="contact-name"
                        className="text-sm font-medium text-foreground-800"
                      >
                        Name
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        placeholder="Your full name"
                        className="px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label
                        htmlFor="contact-phone"
                        className="text-sm font-medium text-foreground-800"
                      >
                        Phone
                      </label>
                      <input
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        required
                        placeholder="Your phone number"
                        className="px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="contact-city"
                      className="text-sm font-medium text-foreground-800"
                    >
                      City / Location
                    </label>
                    <input
                      id="contact-city"
                      name="city"
                      type="text"
                      required
                      placeholder="Your city"
                      className="px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="contact-message"
                      className="text-sm font-medium text-foreground-800"
                    >
                      Message
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      placeholder="Tell us how we can help..."
                      className="px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-secondary-500 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            <p className="text-center text-xs text-foreground-400 mt-6">
              Based in Meerut, Uttar Pradesh. We typically reply within 24 hours.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}